import React, { useState, memo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import moment from 'moment-timezone';
import {
	useAssignExternalBundleProfessionalMutation,
	useAssignExternalProfessionalMutation
} from '../../../../redux/externalAgencyShifts';
import AgencyAssign from './AgencyAssign';

import './AgencyAssignModal.css';
import AgencyAssignOTBypassModal from './AgencyAssignOTBypassModal';

const AgencyAssignModal = ({
	dateTemplate = () => {},
	shift = null,
	bundle = null,
	bundleShifts = {},
	onCancel = () => {},
	onComplete,
	showNotification
}) => {
	const [assignExternalProfessional] = useAssignExternalProfessionalMutation();
	const [assignExternalBundleProfessional] = useAssignExternalBundleProfessionalMutation();
	const [selectedCaregivers, setSelectedCaregivers] = useState([]);
	const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
	const [showExternalBypassModal, setShowExternalBypassModal] = useState(false);

	const addCaregiver = (caregiver, clearSelectedCaregivers) => {
		if (clearSelectedCaregivers) {
			setSelectedCaregivers([caregiver]);
			return;
		}

		const found = selectedCaregivers.find((c) => c.caregiver_id === caregiver.caregiver_id);
		if (!found) {
			setSelectedCaregivers([...selectedCaregivers, ...[caregiver]]);
		}
	};

	const removeCaregiver = (caregiver) => {
		const newSelectedCaregiver = selectedCaregivers.filter((c) => c !== caregiver);
		setSelectedCaregivers(newSelectedCaregiver);
	};

	let handleAssign;
	let data;
	let credentialCheckError;
	if (shift) {
		data = shift;
		const shiftId = shift.is_recurring ? shift.recurring_shift_id : shift.shift_id;
		handleAssign = async (byPassOTModalConfirmation) => {
			setShowLoadingSpinner(true);
			const [caregiver] = selectedCaregivers;
			try {
				await assignExternalProfessional({
					shiftId,
					caregiverId: caregiver.caregiver_id,
					shiftData: !shift.is_recurring
						? {
								isGuaranteed: shift.is_guaranteed
						  }
						: null,
					recurringShiftData: shift.is_recurring
						? {
								shiftId: shift.recurring_shift_id,
								shiftDate: moment.tz(shift.start_time, shift.facility_timezone).format('YYYY-MM-DD'),
								facilityId: shift.facility_id,
								isGuaranteed: shift.is_guaranteed
						  }
						: null,
					byPassOTModalConfirmation
				}).unwrap();
				setTimeout(() => {
					showNotification(
						'info',
						'This assigned shift will only be visible on the Assigned tab when you refresh or navigate away from this feed.'
					);
				}, 1600);
				onComplete({ ...shift, caregiverName: caregiver.caregiver_name });
			} catch (error) {
				if (error.data.showExternalBypassModal) {
					setShowExternalBypassModal(error.data.showExternalBypassModal);
				} else {
					let message = 'Sorry, there was an error trying to assign this shift. Please try again.';
					if (typeof error.data?.errors?.[0]?.message === 'string') {
						message = error.data?.errors?.[0]?.message;
					} else if (typeof error.data?.errors?.[0] === 'object') {
						message =
							"The assignment could not be completed due to pending, missing, or rejected credentials. Please refer to your credential sheet and check this professional's credentials to determine if all expected credentials have been submitted and verified.";
						credentialCheckError = true;
					}
					showNotification('error', message);
					onComplete({ ...shift, error: true, errorField: error.data?.errors?.[0]?.field, credentialCheckError });
				}
			}
		};
	}

	if (bundle) {
		data = bundle;
		handleAssign = async (byPassOTModalConfirmation) => {
			setShowLoadingSpinner(true);
			const [caregiver] = selectedCaregivers;
			try {
				await assignExternalBundleProfessional({
					bundleId: bundle.contract_id,
					caregiverId: caregiver.caregiver_id,
					shiftsData: bundleShifts.map((el) => {
						return { slotId: el.slot_id, isGuaranteed: el.is_guaranteed };
					}),
					byPassOTModalConfirmation
				}).unwrap();
				setTimeout(() => {
					showNotification(
						'info',
						'This assigned bundle will only be visible on the Assigned tab when you refresh or navigate away from this feed.'
					);
				}, 1600);
				onComplete({ ...bundle, caregiverName: caregiver.caregiver_name });
			} catch (error) {
				if (error.data.showExternalBypassModal) {
					setShowExternalBypassModal(error.data.showExternalBypassModal);
				} else {
					let message = 'Sorry, there was an error trying to assign this bundle. Please try again.';
					if (typeof error.data?.errors?.[0]?.message === 'string') {
						message = error.data?.errors?.[0]?.message;
					} else if (typeof error.data?.errors?.[0] === 'object') {
						message =
							"The assignment could not be completed due to pending, missing, or rejected credentials. Please refer to your credential sheet and check this professional's credentials to determine if all expected credentials have been submitted and verified.";
						credentialCheckError = true;
					}
					showNotification('error', message);
					onComplete({ ...bundle, error: true, errorField: error.data?.errors?.[0]?.field, credentialCheckError });
				}
			}
		};
	}

	const handleOTBypassCancel = async () => {
		setShowExternalBypassModal(false);
		setShowLoadingSpinner(false);
	};
	const handleOTBypassAssign = async () => {
		setShowExternalBypassModal(false);
		handleAssign(true);
	};

	return [
		<Modal size="lg" show className="assign-professional-modal no-gutters" onHide={onCancel}>
			<Modal.Header closeButton>
				<Modal.Title>
					<b>Assign {data.shift_type}</b> - <span>{dateTemplate(data)}</span>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<AgencyAssign
					shift={shift}
					bundle={bundle}
					removeCaregiver={removeCaregiver}
					addCaregiver={addCaregiver}
					selectedCaregivers={selectedCaregivers}
					showLoadingSpinner={showLoadingSpinner}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onCancel} variant="tertiary" disabled={false}>
					Cancel
				</Button>
				<Button
					variant="primary"
					disabled={selectedCaregivers.length === 0}
					className="no-gutters"
					onClick={() => {
						handleAssign();
					}}
				>
					Assign professional
				</Button>
			</Modal.Footer>
		</Modal>,
		showExternalBypassModal && (
			<AgencyAssignOTBypassModal
				handleOTBypassCancel={handleOTBypassCancel}
				handleOTBypassAssign={handleOTBypassAssign}
				caregiverName={selectedCaregivers[0].caregiver_name}
			/>
		)
	];
};

export default memo(AgencyAssignModal);
